import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { showSuccess } from '../components/GlobalSnackbar';
import { formatFen } from '../utils/money';

export default function MaterialList() {
  const [materials, setMaterials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [newMaterial, setNewMaterial] = useState({
    projectId: '',
    date: new Date().toISOString().slice(0, 10),
    materialName: '',
    specification: '',
    unit: '',
    quantity: '',
    unitPrice: '',
    supplier: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [materialsRes, projectsRes] = await Promise.all([
        api.get('/api/materials'),
        api.get('/api/projects?pageSize=500'),
      ]);
      setMaterials(materialsRes.data.data || materialsRes.data);
      setProjects(projectsRes.data.data || projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.error || error.message || '加载材料记录失败');
    } finally {
      setLoading(false);
    }
  };

  const submitLock = useRef(false); // 写锁：与 QuotationDetail.writeLock 同款防连点

  const handleCreate = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const quantity = parseFloat(newMaterial.quantity) || 0;
        const unitPrice = Math.round(parseFloat(newMaterial.unitPrice || 0) * 100);
        const totalAmount = Math.round(quantity * unitPrice);

        const material = {
          id: Date.now().toString(),
          ...newMaterial,
          quantity,
          unitPrice,
          totalAmount,
          paymentStatus: 0,
        };
        await api.post('/api/materials', material);
        setOpenDialog(false);
        resetForm();
        showSuccess('材料已保存');
        fetchData();
      } catch (error) {
        console.error('Error creating material:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleEditStart = (item) => {
    setEditingItem(item);
    setNewMaterial({
      projectId: item.projectId || '',
      date: item.date || '',
      materialName: item.materialName || '',
      specification: item.specification || '',
      unit: item.unit || '',
      quantity: item.quantity ? String(item.quantity) : '',
      unitPrice: item.unitPrice ? String(item.unitPrice / 100) : '',
      supplier: item.supplier || '',
      notes: item.notes || '',
    });
    setOpenDialog(true);
  };

  const handleEditSave = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const quantity = parseFloat(newMaterial.quantity) || 0;
        const unitPrice = Math.round(parseFloat(newMaterial.unitPrice || 0) * 100);
        const totalAmount = Math.round(quantity * unitPrice);

        const data = {
          ...newMaterial,
          quantity,
          unitPrice,
          totalAmount,
        };
        await api.put(`/api/materials/${editingItem.id}`, data);
        setOpenDialog(false);
        setEditingItem(null);
        resetForm();
        showSuccess('材料已更新');
        fetchData();
      } catch (error) {
        console.error('Error updating material:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/materials/${confirmDialog.id}`);
      setConfirmDialog({ open: false, id: null });
      fetchData();
      showSuccess('材料记录已删除');
    } catch (error) {
      console.error('Error deleting material:', error);
    }
  };

  const resetForm = () => {
    setNewMaterial({
      projectId: '',
      date: new Date().toISOString().slice(0, 10),
      materialName: '',
      specification: '',
      unit: '',
      quantity: '',
      unitPrice: '',
      supplier: '',
      notes: '',
    });
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>材料管理</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchData}>重新加载</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">材料管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingItem(null); resetForm(); setOpenDialog(true); }}
        >
          新增材料
        </Button>
      </Box>

      {materials.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无材料记录
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>日期</TableCell>
                <TableCell>项目</TableCell>
                <TableCell>材料名称</TableCell>
                <TableCell>规格</TableCell>
                <TableCell align="right">数量</TableCell>
                <TableCell>单位</TableCell>
                <TableCell align="right">单价</TableCell>
                <TableCell align="right">总价</TableCell>
                <TableCell>供应商</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materials.map((material) => (
                <TableRow key={material.id}>
                  <TableCell>{material.date}</TableCell>
                  <TableCell>{material.projectName || '-'}</TableCell>
                  <TableCell>{material.materialName}</TableCell>
                  <TableCell>{material.specification || '-'}</TableCell>
                  <TableCell align="right">{material.quantity}</TableCell>
                  <TableCell>{material.unit || '-'}</TableCell>
                  <TableCell align="right">{formatFen(material.unitPrice)}</TableCell>
                  <TableCell align="right">{formatFen(material.totalAmount)}</TableCell>
                  <TableCell>{material.supplier || '-'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditStart(material)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(material.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setEditingItem(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? '编辑材料' : '新增材料'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="项目"
            fullWidth
            variant="outlined"
            value={newMaterial.projectId}
            onChange={(e) => setNewMaterial({ ...newMaterial, projectId: e.target.value })}
            sx={{ mb: 2 }}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="日期"
            fullWidth
            variant="outlined"
            type="date"
            value={newMaterial.date}
            onChange={(e) => setNewMaterial({ ...newMaterial, date: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="材料名称"
            fullWidth
            variant="outlined"
            value={newMaterial.materialName}
            onChange={(e) => setNewMaterial({ ...newMaterial, materialName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="规格"
            fullWidth
            variant="outlined"
            value={newMaterial.specification}
            onChange={(e) => setNewMaterial({ ...newMaterial, specification: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                margin="dense"
                label="数量"
                fullWidth
                variant="outlined"
                type="number"
                value={newMaterial.quantity}
                onChange={(e) => setNewMaterial({ ...newMaterial, quantity: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                margin="dense"
                label="单位"
                fullWidth
                variant="outlined"
                value={newMaterial.unit}
                onChange={(e) => setNewMaterial({ ...newMaterial, unit: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                margin="dense"
                label="单价（元）"
                fullWidth
                variant="outlined"
                type="number"
                value={newMaterial.unitPrice}
                onChange={(e) => setNewMaterial({ ...newMaterial, unitPrice: e.target.value })}
              />
            </Grid>
          </Grid>
          <TextField
            margin="dense"
            label="供应商"
            fullWidth
            variant="outlined"
            value={newMaterial.supplier}
            onChange={(e) => setNewMaterial({ ...newMaterial, supplier: e.target.value })}
            sx={{ mb: 2, mt: 2 }}
          />
          <TextField
            margin="dense"
            label="备注"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={newMaterial.notes}
            onChange={(e) => setNewMaterial({ ...newMaterial, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setEditingItem(null); }}>取消</Button>
          <Button onClick={editingItem ? handleEditSave : handleCreate} variant="contained" disabled={!newMaterial.projectId || !newMaterial.materialName || !(parseFloat(newMaterial.quantity) > 0) || !(parseFloat(newMaterial.unitPrice) > 0)}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="确认删除"
        message="确定要删除这条材料记录吗？"
      />
    </Box>
  );
}
